import React, { useEffect, useState } from "react";
import { useFirestoreConnect, isLoaded, isEmpty } from "react-redux-firebase";
import { useSelector } from "react-redux";
import LoadingIndicator from "../../../components/common/LoadingIndicator";

import moment from "moment";
import "moment/locale/es";
import SortableTable from "../../../components/common/tables/SortableTable";
import DriverSelector from "../../../components/orders/DriverSelector";
import OrderStatusSelector from "../../../components/orders/OrderStatusSelector";
import BackButton from "../../../components/common/BackButton";

const OrderPage = ({ match }) => {
  const [restaurantId, setRestaurantId] = useState("");
  const { id } = match.params;
  const uid = useSelector(({ firebase }) => firebase.auth.uid);

  const searchRegExp = /\s/g;

  useFirestoreConnect([
    { collection: "restaurants", where: [["user_id", "==", uid]] },
    {
      collection: "restaurants",
      doc: restaurantId || "nothing",
      subcollections: [{ collection: "orders", doc: id }],
      storeAs: `order-${id}`,
    },
  ]);

  const restaurant = useSelector(({ firestore }) =>
    firestore.ordered.restaurants ? firestore.ordered.restaurants[0] : {}
  );

  const order = useSelector(({ firestore }) => {
    return firestore.data[`order-${id}`];
  });

  useEffect(() => {
    if (isLoaded(restaurant)) setRestaurantId(restaurant.id);
  }, [restaurant]);

  if (!isLoaded(restaurant) || !isLoaded(order))
    return <LoadingIndicator></LoadingIndicator>;

  if (
    isLoaded(order) &&
    isLoaded(restaurant) &&
    (isEmpty(order) || isEmpty(restaurant))
  )
    return <LoadingIndicator></LoadingIndicator>;

  const total =
    typeof order.cost === "string" ? parseFloat(order.cost) : order.cost;

  return (
    <div
      className={`px-16 py-8 text-gray-900 bg-opacity-75 min-h-screen ${
        order.status === "solicitada" ? "bg-gray-100" : ""
      } ${order.status === "aceptada" ? "bg-yellow-100" : ""} ${
        order.status === "cancelada" ? "bg-red-100" : ""
      } ${order.status === "completada" ? "bg-green-100" : ""} ${
        order.status === "en curso" ? "bg-orange-100" : ""
      }`}
    >
      <BackButton></BackButton>
      <h3 className="text-2xl font-bold relative">
        {order.client}
        <p className="w-auto justify-end flex flex-wrap items-baseline text-3xl font-bold absolute top-0 right-0 leading-5">
          <span className="text-sm mr-1 font-normal">$ </span>
          {total.toFixed(2)}{" "}
          <span className="text-xs ml-1 font-normal">MXN</span>
          <span className="w-full text-sm text-right font-light text-gray-600">
            {order.paymentMethod}
          </span>
        </p>
      </h3>
      <p className="text-xs font-light text-gray-600">
        <span className="flex items-center">
          <DotComponent status={order.status}></DotComponent>
          <span className="capitalize mr-1">{order.status}</span>
          {" " + moment(order.dateCreated.toDate()).fromNow()}
        </span>
      </p>
      <a
        href={`https://www.google.com.mx/maps/place/${order.address.replace(
          searchRegExp,
          "+"
        )}`}
        target="_blank"
        rel="nofollow"
        className="mt-3 mb-2 text-sm text-gray-700 font-medium"
      >
        {order.address}
      </a>
      {order.description ? (
        <p className="mt-3 mb-2 text-sm text-gray-700">
          <span className="font-bold text-gray-800 uppercase">Notas:</span>{" "}
          {order.description}
        </p>
      ) : (
        <></>
      )}
      <div className="bg-white mt-4 shadow-sm rounded-sm">
        <p className="px-6 py-3 flex justify-between border">Productos</p>
        <div className="relative text-gray-700 text-md border-none">
          <SortableTable
            NoDataComponent={NoDataComponent}
            columns={columns}
            data={order.products}
            className="table-auto w-full bg-white m-0 border-none"
          ></SortableTable>
        </div>
      </div>
      <h3 className="text-lg font-medium relative mt-3 mb-2 text-gray-900">
        Repartidor
      </h3>
      <DriverSelector
        selected={order.driverID}
        restaurantId={restaurantId}
        orderId={id}
        userId={order.clientID}
      ></DriverSelector>
      <h3 className="text-lg font-medium relative mt-3 mb-2 text-gray-900">
        Cambiar estado de la orden
      </h3>
      <OrderStatusSelector
        selected={order.status}
        restaurantId={restaurantId}
        orderId={id}
        userId={order.clientID}
      ></OrderStatusSelector>
    </div>
  );
};

const DotComponent = ({ status }) => {
  let color;

  switch (status) {
    case "solicitada":
      color = "bg-gray-500";
      break;

    case "completada":
      color = "bg-green-500";
      break;

    case "aceptada":
      color = "bg-yellow-500";
      break;

    case "en curso":
      color = "bg-orange-500";
      break;

    case "cancelada":
      color = "bg-red-500";
      break;

    default:
      break;
  }
  return (
    <div className={`rounded-full w-2 h-2 ${color} inline-block mr-1`}></div>
  );
};

const columns = [
  {
    Header: "Nombre",
    accessor: "product.title",
  },
  {
    Header: "Cantidad",
    accessor: "quantity",
  },
  {
    Header: "Precio",
    accessor: (values) => {
      const total =
        typeof values.product.price === "string"
          ? parseFloat(values.product.price)
          : values.product.price;

      return total;
    },
  },
  {
    Header: "Subtotal",
    accessor: (values) => {
      return values.quantity * values.product.price;
    },
  },
  {
    Header: "Toppings",
    accessor: (values) => {
      const { toppings } = values;
      let s = "";

      Object.keys(toppings).forEach((key, i) => {
        const options = toppings[key];
        if (i > 0) s += "; ";
        s += key + ": ";

        options.forEach((option, index) => {
          const total =
            typeof option.cost === "string"
              ? parseFloat(option.cost)
              : option.cost;
          if (index > 0) s += ", ";
          s += `${option.name} ($${total})`;
        });
      });

      return s;
    },
  },
  {
    Header: "Total",
    accessor: (values) => {
      const { toppings } = values;
      let t = 0;

      Object.keys(toppings).forEach((key, i) => {
        const options = toppings[key];
        options.forEach((option, index) => {
          const total =
            typeof option.cost === "string"
              ? parseFloat(option.cost)
              : option.cost;
          t += total;
        });
      });

      return values.quantity * values.product.price + t * values.quantity;
    },
  },
];

const NoDataComponent = () => {
  return (
    <div className="flex justify-center bg-white shadow-md py-16 flex-wrap text-gray-700">
      <h3 className="text-center text-4xl w-full font-bold">
        El usuario aún no cuenta con restaurantes
      </h3>
    </div>
  );
};

export default OrderPage;
